/**
 * RefOrValue — universal SDK component for toggling between manual config
 * input and a $ref{} reference that replaces one or more config fields.
 */

import {
    type FC,
    type ReactNode,
    useCallback,
    useEffect,
    useState,
} from "react";
import type { ReferenceBrowserContext } from "./ReferenceBrowser";
import { ReferenceBrowser } from "./ReferenceBrowser";
import {
    deleteConfigValueAtPath,
    getConfigValueAtPath,
    isRefValue,
    setConfigValueAtPath,
} from "./refvalue-helpers";
import { usePluginSdkT } from "../i18n";

export interface RefOrValueProps {
    label: ReactNode;
    refField: string;
    manualFields?: string[];
    config: Record<string, unknown>;
    onChange: (newConfig: Record<string, unknown>) => void;
    context: ReferenceBrowserContext;
    refHint?: string;
    borderless?: boolean;
    refOnly?: boolean;
    inline?: boolean;
    children?: ReactNode;
}

const PencilIcon: FC<{ className?: string }> = ({ className }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
        focusable="false"
    >
        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
);

const LinkIcon: FC<{ className?: string }> = ({ className }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
        focusable="false"
    >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
);

export const RefOrValue: FC<RefOrValueProps> = ({
    label,
    refField,
    manualFields,
    config,
    onChange,
    context,
    refHint,
    borderless,
    refOnly,
    inline,
    children,
}) => {
    const t = usePluginSdkT();
    const currentRef = getConfigValueAtPath(config, refField);
    const isRef = isRefValue(currentRef);

    const [mode, setMode] = useState<"manual" | "ref">(
        refOnly || isRef ? "ref" : "manual",
    );

    useEffect(() => {
        setMode(refOnly || isRef ? "ref" : "manual");
    }, [isRef, refOnly]);

    const switchToRef = useCallback(() => {
        setMode("ref");
    }, []);

    const switchToManual = useCallback(() => {
        setMode("manual");
        if (currentRef !== undefined) {
            onChange(deleteConfigValueAtPath(config, refField));
        }
    }, [config, currentRef, refField, onChange]);

    const handleSelectRef = useCallback(
        (refPath: string) => {
            let next = setConfigValueAtPath(config, refField, refPath);
            if (manualFields) {
                for (const key of manualFields) {
                    if (key === refField) continue;
                    next = deleteConfigValueAtPath(next, key);
                }
            }
            onChange(next);
            setMode("ref");
        },
        [config, refField, manualFields, onChange],
    );

    const toggleButton = !refOnly ? (
        <button
            type="button"
            className={`shrink-0 rounded p-1 transition-colors ${mode === "ref"
                ? "text-primary hover:bg-primary/10"
                : "text-foreground/50 hover:bg-muted hover:text-foreground/80"
            }`}
            onClick={mode === "manual" ? switchToRef : switchToManual}
            title={mode === "manual" ? t("refOrValue.switchToRef") : t("refOrValue.switchToManual")}
        >
            {mode === "manual" ? <PencilIcon /> : <LinkIcon />}
        </button>
    ) : null;

    const refContent = (
        <ReferenceBrowser
            value={typeof currentRef === "string" ? currentRef : undefined}
            onChange={handleSelectRef}
            context={context}
            hint={refHint}
            borderless
            inline={inline}
        />
    );

    if (inline) {
        const valueContent = mode === "manual" ? <>{children}</> : refContent;

        return (
            <div className="w-full">
                {label ? (
                    <div className="flex items-center px-0 py-1">
                        <span className="text-[11px] font-semibold text-foreground/70 flex-1">
                            {label}
                        </span>
                    </div>
                ) : null}
                <div className={`flex min-w-0 items-center${toggleButton ? " gap-1" : ""}`}>
                    {toggleButton}
                    <div className="min-w-0 flex-1">
                        {valueContent}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            {!refOnly || label ? (
                <div className={`flex items-center px-0 py-1.5${label ? " gap-2" : ""}`}>
                    {label ? (
                        <span className="text-[11px] font-semibold text-foreground/70 flex-1">
                            {label}
                        </span>
                    ) : null}
                    {toggleButton}
                </div>
            ) : null}

            <div
                className={
                    borderless
                        ? ""
                        : "rounded-lg border border-border/60 bg-muted/20 px-3 py-2"
                }
            >
                {mode === "manual" ? <div className="space-y-2">{children}</div> : refContent}
            </div>
        </div>
    );
};
